// src/entities/payment/api/stripeMutations.ts
import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { mutator } from '@/shared/api';
import { handleSuccess } from '@/shared/lib/success';
import { paymentKeys } from './queryKeys';
import type {
  StripeOnboardResponse,
  StripeDashboardLinkResponse,
  StripeRefundResponse,
} from '../model/stripeTypes';

// ── Enable / Disable ────────────────────────────────────────────────────────

export function useEnableStorePayment() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { paymentId: string }>({
    mutationFn: ({ paymentId }) =>
      mutator('POST', `/payments/${paymentId}/enable`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.merchantAll });
    },
  });
}

export function useDisableStorePayment() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { paymentId: string }>({
    mutationFn: ({ paymentId }) =>
      mutator('POST', `/payments/${paymentId}/disable`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.merchantAll });
    },
  });
}

// ── Onboarding ───────────────────────────────────────────────────────────────

interface OnboardInput {
  paymentId: string;
  returnUrl: string;
  refreshUrl: string;
}

export function useStripeOnboard() {
  return useMutation<StripeOnboardResponse, Error, OnboardInput>({
    mutationFn: async ({ paymentId, returnUrl, refreshUrl }) => {
      const res = await mutator<{ data: StripeOnboardResponse }>(
        'POST',
        `/payments/${paymentId}/onboard`,
        { returnUrl, refreshUrl },
      );
      return res.data;
    },
  });
}

export function useStripeOnboardRefresh() {
  return useMutation<StripeOnboardResponse, Error, OnboardInput>({
    mutationFn: async ({ paymentId, returnUrl, refreshUrl }) => {
      const res = await mutator<{ data: StripeOnboardResponse }>(
        'POST',
        `/payments/${paymentId}/onboard/refresh`,
        { returnUrl, refreshUrl },
      );
      return res.data;
    },
  });
}

// ── Dashboard Link (fetch on demand, no cache) ───────────────────────────────

export function useStripeDashboardLink() {
  return useMutation<StripeDashboardLinkResponse, Error, { paymentId: string }>(
    {
      mutationFn: async ({ paymentId }) => {
        const res = await mutator<{ data: StripeDashboardLinkResponse }>(
          'GET',
          `/payments/${paymentId}/dashboard-link`,
        );
        return res.data;
      },
    },
  );
}

// ── Refund ───────────────────────────────────────────────────────────────────

interface RefundInput {
  paymentId: string;
  transactionId: string;
  amount?: number;   // omit for full refund
  reason?: string;
}

// Cross-entity invalidation (orders, transactions) is the caller's responsibility —
// pass an `onSuccess` via options. Same-layer entity imports are forbidden by FSD,
// so the caller (a feature or widget) wires the broader cache refresh.
export function useIssueRefund(
  options?: Omit<
    UseMutationOptions<StripeRefundResponse, Error, RefundInput>,
    'mutationFn'
  >,
) {
  return useMutation<StripeRefundResponse, Error, RefundInput>({
    mutationFn: ({ paymentId, transactionId, amount, reason }) =>
      mutator(
        'POST',
        `/payments/${paymentId}/transactions/${transactionId}/refund`,
        { ...(amount !== undefined && { amount }), ...(reason && { reason }) },
      ),
    ...options,
    onSuccess: (data, variables, context) => {
      handleSuccess(data, { titleKey: 'success.payment.refundIssuedSuccessfully' });
      options?.onSuccess?.(data, variables, context);
    },
  });
}
